import './styles.css'
import { miniApp, useSignal } from '@tma.js/sdk-react'
import { useEffect, useState } from 'react'
import { AgencyOrderPage } from '@/components/AgencyOrderPage'
import { BottomNavigation } from '@/components/BottomNavigation'
import { ContactManagerPage } from '@/components/ContactManagerPage'
import DocsForm from '@/components/DocsForm'
import { RateSettingsPage } from '@/components/RateSettingsPage'
import { RatesPage } from '@/components/RatesPage'
import { ReferralPage } from '@/components/ReferralPage'
import { RequestInfoPage } from '@/components/RequestInfoPage'
import { RequestsListPage } from '@/components/RequestsListPage'
import { StatisticsPage } from '@/components/StatisticsPage'
import { TelegramBackButton } from '@/components/TelegramBackButton'
import { TelegramSettingsButton } from '@/components/TelegramSettingsButton'
import { type ApiMe, fetchMe } from '@/fetcher'
import { init } from './init'
import { NavProvider, useNav } from './nav'

init()

const AppShell = () => {
  const { state, navigate, back } = useNav()
  const [me, setMe] = useState<ApiMe | null>(null)
  const [isLoadingMe, setIsLoadingMe] = useState(true)

  useEffect(() => {
    let isMounted = true

    fetchMe()
      .then((payload) => {
        if (isMounted) setMe(payload)
      })
      .catch(() => {
        if (isMounted) setMe(null)
      })
      .finally(() => {
        if (isMounted) setIsLoadingMe(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  const isAdmin = me?.user.role === 'admin'
  const isManagerOrAdmin =
    me?.user.role === 'admin' || me?.user.role === 'manager'
  const hasNoAccess = me
    ? me.user.role === 'user' || me.teams.length === 0
    : false
  const showBottomNavigation =
    me &&
    isManagerOrAdmin &&
    ['requests-list', 'statistics', 'rates', 'referral'].includes(state.view)
  const showSettingsButton = isAdmin
  const showBackButton =
    state.view !== 'requests-list' &&
    state.view !== 'docs' &&
    !showBottomNavigation

  return (
    <div className="min-h-screen bg-[var(--app-bg)] pb-24 text-[var(--app-text)]">
      {showBackButton && <TelegramBackButton isVisible onClick={back} />}

      <TelegramSettingsButton
        isVisible={showSettingsButton}
        onClick={() => navigate('settings')}
      />

      {hasNoAccess ? (
        <ContactManagerPage />
      ) : state.view === 'agency-order' && me && isAdmin ? (
        <AgencyOrderPage />
      ) : state.view === 'request-info' && me && isManagerOrAdmin ? (
        <RequestInfoPage requestId={state.params.requestId ?? ''} />
      ) : state.view === 'requests-list' && me && isManagerOrAdmin ? (
        <RequestsListPage me={me} />
      ) : state.view === 'statistics' && me && isManagerOrAdmin ? (
        <StatisticsPage me={me} />
      ) : state.view === 'rates' && me && isManagerOrAdmin ? (
        <RatesPage me={me} />
      ) : state.view === 'referral' && me && isManagerOrAdmin ? (
        <ReferralPage />
      ) : state.view === 'settings' && me && isAdmin ? (
        <RateSettingsPage me={me} isAdmin={isAdmin} />
      ) : (
        <DocsForm me={me} isLoadingMe={isLoadingMe} />
      )}
      {showBottomNavigation && <BottomNavigation />}
    </div>
  )
}

const App = () => {
  const isDark = useSignal(miniApp.isDark)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light'
  }, [isDark])

  return (
    <NavProvider>
      <AppShell />
    </NavProvider>
  )
}

export default App
